/**
 * Domestic Number Patterns for Key Countries
 * 
 * These patterns detect local/domestic phone number formats and convert them to E.164.
 * Priority: More specific patterns (like Australian mobiles) are checked before
 * more general patterns to avoid conflicts (e.g., AU 04XX vs NZ 04X).
 */

export interface DomesticPatternRule {
  regex: RegExp;
  type: "mobile" | "landline" | "geographic" | "nanp" | "non-geographic";
  description: string;
  stripLeadingZero: boolean;
  /** For patterns where we need to strip more than just the leading zero */
  stripPrefix?: number;
}

export interface DomesticPattern {
  countryCode: string;
  countryName: string;
  flag: string;
  /** Priority order - lower numbers are checked first */
  priority: number;
  patterns: DomesticPatternRule[];
}

/**
 * Domestic patterns ordered by priority.
 * Australia is checked first to handle 04XX mobile numbers before NZ's shorter 04 pattern.
 */
export const DOMESTIC_PATTERNS: DomesticPattern[] = [
  // 🇦🇺 Australia (Country Code: 61)
  // Australian numbers are 10 digits total (including area code)
  // Mobile: 04XX XXX XXX
  // Landline: 0X XXXX XXXX (where X is 2, 3, 7, or 8)
  {
    countryCode: "61",
    countryName: "Australia",
    flag: "🇦🇺",
    priority: 1, // Check first due to 10-digit format
    patterns: [
      // Mobile - 04XX XXX XXX (10 digits)
      { regex: /^04\d{8}$/, type: "mobile", description: "Mobile", stripLeadingZero: true },
      // NSW/ACT - 02 XXXX XXXX
      { regex: /^02\d{8}$/, type: "landline", description: "NSW/ACT", stripLeadingZero: true },
      // VIC/TAS - 03 XXXX XXXX
      { regex: /^03\d{8}$/, type: "landline", description: "VIC/TAS", stripLeadingZero: true },
      // QLD - 07 XXXX XXXX
      { regex: /^07\d{8}$/, type: "landline", description: "QLD", stripLeadingZero: true },
      // SA/WA/NT - 08 XXXX XXXX
      { regex: /^08\d{8}$/, type: "landline", description: "SA/WA/NT", stripLeadingZero: true },
    ],
  },

  // 🇳🇿 New Zealand (Country Code: 64)
  // NZ numbers are typically 9-10 digits
  // Mobile: 02X XXX XXXX (9-10 digits)
  // Geographic: 0X XXX XXXX (9 digits, X is 3, 4, 6, 7, or 9)
  {
    countryCode: "64",
    countryName: "New Zealand",
    flag: "🇳🇿",
    priority: 2, // After AU due to similar patterns but different lengths
    patterns: [
      // Mobile - 021, 022, 027, 028, 029 (9-10 digits)
      { regex: /^02[1-9]\d{6,7}$/, type: "mobile", description: "Mobile", stripLeadingZero: true },
      // Auckland - 09 XXX XXXX (9 digits)
      { regex: /^09\d{7}$/, type: "geographic", description: "Auckland", stripLeadingZero: true },
      // Waikato/BOP - 07 XXX XXXX (9 digits)
      { regex: /^07\d{7}$/, type: "geographic", description: "Waikato/BOP", stripLeadingZero: true },
      // East/Central NI - 06 XXX XXXX (9 digits)
      { regex: /^06\d{7}$/, type: "geographic", description: "Central NI", stripLeadingZero: true },
      // Wellington - 04 XXX XXXX (9 digits) - Note: 9 digits vs AU's 10
      { regex: /^04\d{7}$/, type: "geographic", description: "Wellington", stripLeadingZero: true },
      // South Island - 03 XXX XXXX (9 digits)
      { regex: /^03\d{7}$/, type: "geographic", description: "South Island", stripLeadingZero: true },
    ],
  },

  // 🇬🇧 United Kingdom (Country Code: 44)
  // UK numbers are 10-11 digits (after the leading 0)
  // Mobile: 07XXX XXXXXX (11 digits)
  // Geographic: 01XXX XXXXXX or 02X XXXX XXXX (10-11 digits)
  {
    countryCode: "44",
    countryName: "United Kingdom",
    flag: "🇬🇧",
    priority: 3,
    patterns: [
      // Mobile - 07XXX XXXXXX (11 digits starting with 07)
      { regex: /^07\d{9}$/, type: "mobile", description: "Mobile", stripLeadingZero: true },
      // London - 020 XXXX XXXX (11 digits)
      { regex: /^020\d{8}$/, type: "geographic", description: "London", stripLeadingZero: true },
      // Geographic - 01XXX (10-11 digits)
      { regex: /^01\d{8,9}$/, type: "geographic", description: "Geographic", stripLeadingZero: true },
      // Cardiff/Birmingham etc - 02X (11 digits)
      { regex: /^02[1-9]\d{8}$/, type: "geographic", description: "Major Cities", stripLeadingZero: true },
      // Non-geographic - 03XX (11 digits)
      { regex: /^03\d{9}$/, type: "non-geographic", description: "Non-geographic", stripLeadingZero: true },
    ],
  },

  // 🇺🇸 United States (Country Code: 1) - NANP
  // 10-digit format: NPA-NXX-XXXX (area code + exchange + subscriber)
  // 11-digit with leading 1: 1-NPA-NXX-XXXX
  {
    countryCode: "1",
    countryName: "United States",
    flag: "🇺🇸",
    priority: 4,
    patterns: [
      // 11-digit with leading 1: 1XXXXXXXXXX
      { regex: /^1[2-9]\d{9}$/, type: "nanp", description: "US/Canada", stripLeadingZero: false, stripPrefix: 0 },
      // 10-digit NANP: [2-9]XX-XXX-XXXX (area code starts with 2-9)
      { regex: /^[2-9]\d{9}$/, type: "nanp", description: "US/Canada", stripLeadingZero: false },
    ],
  },

  // 🇨🇦 Canada (Country Code: 1) - NANP (same as US)
  // Note: Canada uses the same NANP as US, detection defaults to US
  // Users can manually select Canada if needed
  {
    countryCode: "1",
    countryName: "Canada",
    flag: "🇨🇦",
    priority: 5, // After US - both use NANP, US is default
    patterns: [
      // Same patterns as US - Canada-specific area codes could be added for better detection
      // Common Canadian area codes: 204, 226, 236, 249, 250, 289, 306, 343, 365, 387, 403, 416, 418, 431, 437, 438, 450, 506, 514, 519, 548, 579, 581, 587, 604, 613, 639, 647, 672, 705, 709, 778, 780, 782, 807, 819, 825, 867, 873, 902, 905
      { regex: /^1(204|226|236|249|250|289|306|343|365|387|403|416|418|431|437|438|450|506|514|519|548|579|581|587|604|613|639|647|672|705|709|778|780|782|807|819|825|867|873|902|905)\d{7}$/, type: "nanp", description: "Canada", stripLeadingZero: false, stripPrefix: 0 },
      { regex: /^(204|226|236|249|250|289|306|343|365|387|403|416|418|431|437|438|450|506|514|519|548|579|581|587|604|613|639|647|672|705|709|778|780|782|807|819|825|867|873|902|905)\d{7}$/, type: "nanp", description: "Canada", stripLeadingZero: false },
    ],
  },
];

/**
 * Get all patterns sorted by priority
 */
export function getSortedPatterns(): DomesticPattern[] {
  return [...DOMESTIC_PATTERNS].sort((a, b) => a.priority - b.priority);
}
